import { FlatList } from "@/components/ui/flat-list";
import { Pressable } from "@/components/ui/pressable";
import { VStack } from "@/components/ui/vstack";
import { Text } from "@/components/ui/text";
import {
  useOrgQRControllerList,
  useOrganizationControllerFindOne,
} from "@/generated/api/api";
import { OrganizationDTO, OrganizationQRDTO } from "@/generated/api/models";
import { EmptyState } from "@/screens/common/empty.state";
import { useSession } from "@/utils/session.ctx";
import { useState } from "react";
import { RestaurantQRCodesItemMini } from "./restaurant.qrcodes.item";
import { Spinner } from "@/components/ui/spinner";
import { Fab, FabIcon, FabLabel } from "@/components/ui/fab";
import { Copy, Download, HelpCircle, Plus, Share } from "lucide-react-native";
import { useQRCodeDownloader } from "@/utils/useQRCode";
import { HStack } from "@/components/ui/hstack";
import QRCodeStyled from "react-native-qrcode-styled";
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import { Platform } from "react-native";
import { Heading } from "@/components/ui/heading";
import { Box } from "@/components/ui/box";
import { Divider } from "@/components/ui/divider";
import { RestaurantQRCodesModalCreate } from "./restaurant.qrcodes.modal";
import { RestaurantQRCodesActionSheet } from "./restaurant.qrcodes.action.sheet";
import { QRViewGuideAlert } from "@/screens/common/qr.view.guide.alert";
import { Switch } from "@/components/ui/switch";
import { CloseIcon, Icon } from "@/components/ui/icon";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";

export type RestaurantQRCodesListFullProps = {
  organizationId: string;
};

function HeaderQR(props: { organization: OrganizationDTO }) {
  const [showTipJar, setShowTipJar] = useState(false);
  const [useTipJar, setUseTipJar] = useState(true);

  const { link, qrCodeRef, onCopy, onShare } = useQRCodeDownloader({
    organization: props.organization,
    tipJar: useTipJar,
  });

  return (
    <>
      <VStack className="gap-4">
        <Box className="flex-col md:flex-row items-center md:items-start w-full gap-4">
          <Box className="min-h-[200px] min-w-[200px]">
            <QRCodeStyled
              ref={qrCodeRef}
              data={link}
              padding={0}
              pieceSize={8}
            />
          </Box>
          <VStack className="gap-4 w-full flex-1">
            <VStack className="gap-1 w-full">
              <Heading>{props.organization.name}</Heading>
              <Text size="sm">
                Main QR code linking to your business page. Use this if you
                don’t plan to accept personal tips per worker — for example, as
                an{" "}
                <Text size="sm" bold>
                  alternative to a shared tip jar.
                </Text>
              </Text>
              <HStack className="gap-2 my-1 items-center">
                <Switch
                  value={useTipJar}
                  onToggle={() => setUseTipJar(!useTipJar)}
                />
                <HStack className="gap-1 items-center">
                  <Text size="md">Use as Tip Jar</Text>
                  <Pressable hitSlop={15} onPress={() => setShowTipJar(true)}>
                    <Icon as={HelpCircle} />
                  </Pressable>
                </HStack>
              </HStack>
            </VStack>
            <Box className="flex-row md:flex-col gap-2 md:w-fit justify-stretch">
              <Button onPress={onCopy} variant="outline" className="grow">
                <ButtonIcon
                  as={Copy}
                  size={20}
                  className="text-typography-950"
                />
                <ButtonText>Copy link</ButtonText>
              </Button>
              <Button onPress={onShare} className="grow">
                <ButtonIcon as={Platform.OS === "web" ? Download : Share} />
                <ButtonText>
                  {Platform.OS === "web" ? "Download PNG" : "Share"}
                </ButtonText>
              </Button>
            </Box>
          </VStack>
        </Box>
        <QRViewGuideAlert />
      </VStack>
      <TipJarModal isOpen={showTipJar} onClose={() => setShowTipJar(false)} />
    </>
  );
}

function TipJarModal(props: Pick<InterfaceModalProps, "onClose" | "isOpen">) {
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            Use as Tip Jar
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Text>
              When Use as Tip Jar is enabled, this QR code will take guests
              directly to the tipping page, without asking them to choose a
              specific team member.
            </Text>
            <Text>
              This is useful when tips are meant to be shared collectively or
              distributed later.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onPress={props.onClose}>
            <ButtonText>Understood</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function RestaurantQRCodesListFull(
  props: RestaurantQRCodesListFullProps
) {
  const {
    data: response,
    isLoadingError,
    isRefetching,
    refetch,
  } = useOrgQRControllerList(props.organizationId);

  const [sheetIsOpen, setSheetIsOpen] = useState(false);
  const [sheetQR, setSheetQR] = useState<OrganizationQRDTO>();
  const [showCreationModal, setShowCreationModal] = useState(false);

  const { data: organization } = useOrganizationControllerFindOne(
    props.organizationId
  );

  return (
    <>
      <FlatList
        className="w-full h-full"
        contentContainerClassName="grow gap-4 p-4"
        data={response}
        keyExtractor={(info) => info.id}
        refreshing={isRefetching}
        onRefresh={refetch}
        renderItem={(info) => {
          return (
            <Pressable
              className="w-full"
              onPress={() => {
                setSheetQR(info.item);
                setSheetIsOpen(true);
              }}
            >
              <RestaurantQRCodesItemMini qr={info.item} />
            </Pressable>
          );
        }}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={() => {
          if (!organization) {
            return <Spinner size="large" />;
          }

          return (
            <VStack className="gap-6">
              <HeaderQR organization={organization} />
              <VStack className="gap-1">
                <Heading>Custom QR codes</Heading>
                <Text size="sm">
                  You may create unlimited number of custom QR codes, which can
                  be used then to granularly setup distribution of tips in
                  sharing scheme
                </Text>
              </VStack>
            </VStack>
          );
        }}
        ListEmptyComponent={() => (
          <VStack className="h-full w-full p-3">
            {response?.length === 0 ? (
              <EmptyState title="No custom QR codes created" />
            ) : isLoadingError ? (
              <EmptyState
                title="Something went wrong"
                description="Please check your internet connection and try again"
              />
            ) : (
              <Spinner size="large" />
            )}
          </VStack>
        )}
      />
      <Fab
        size="md"
        placement="bottom right"
        isHovered={false}
        isDisabled={false}
        isPressed={false}
        onPress={() => setShowCreationModal(true)}
      >
        <FabIcon as={Plus} />
        <FabLabel>Create</FabLabel>
      </Fab>
      <RestaurantQRCodesModalCreate
        organizationId={props.organizationId}
        isOpen={showCreationModal}
        onClose={() => setShowCreationModal(false)}
      />
      <RestaurantQRCodesActionSheet
        organizationId={props.organizationId}
        isOpen={sheetIsOpen}
        qr={sheetQR}
        onClose={() => setSheetIsOpen(false)}
      />
    </>
  );
}
